import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { X } from 'lucide-react'
import { api } from '../lib/api'
import { formatCurrency, formatDate, formatDateTime, STATUS_LABELS, STATUS_COLORS } from '../lib/utils'
import { useAuth } from '../context/AuthContext'
import CsvControls from '../components/CsvControls'

interface Customer {
  id: string; full_name: string; phone: string; plan: string
  loyalty_points: number; drops_balance: number; frozen: number; created_at: string
  order_count: number; total_spent_fils: number
}

interface CustomerOrder {
  id: string; order_number: string; total_fils: number; status: string; created_at: string
}

interface CustomerDetail {
  customer: Customer
  orders: CustomerOrder[]
}

function CustomerPanel({
  customerId,
  onClose,
  canManage,
}: {
  customerId: string
  onClose: () => void
  canManage: boolean
}) {
  const qc = useQueryClient()

  const { data, isLoading } = useQuery<CustomerDetail>({
    queryKey: ['customer-detail', customerId],
    queryFn: () => api.get(`/customers/${customerId}`).then(r => r.data),
  })

  const toggleFreeze = useMutation({
    mutationFn: ({ id, frozen }: { id: string; frozen: boolean }) =>
      api.patch(`/customers/${id}/freeze`, { frozen }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customers'] })
      qc.invalidateQueries({ queryKey: ['customer-detail', customerId] })
    },
  })

  const c = data?.customer

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-md bg-[#111] border-l border-[#2e2e2e] h-full overflow-y-auto flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-[#2e2e2e] sticky top-0 bg-[#111] z-10">
          <div>
            <p className="text-white font-semibold">{c?.full_name ?? '…'}</p>
            <p className="text-gray-500 text-xs mt-0.5">{c?.phone}</p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors p-1 mt-0.5">
            <X size={18} />
          </button>
        </div>

        {isLoading && (
          <div className="flex-1 flex items-center justify-center text-gray-600 text-sm">Loading…</div>
        )}

        {c && (
          <div className="p-5 space-y-5">
            {/* Stats strip — 2x2 so drops sit alongside points (both are
                customer-visible ledgers; surfacing both keeps ops aligned
                with what the customer sees in-app and at the till). */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-[#1a1a1a] rounded-lg px-3 py-3 text-center">
                <p className="text-xl font-semibold text-white">{c.order_count}</p>
                <p className="text-gray-600 text-xs mt-0.5">Orders</p>
              </div>
              <div className="bg-[#1a1a1a] rounded-lg px-3 py-3 text-center">
                <p className="text-lg font-semibold text-white">{formatCurrency(c.total_spent_fils)}</p>
                <p className="text-gray-600 text-xs mt-0.5">Total Spent</p>
              </div>
              <div className="bg-[#1a1a1a] rounded-lg px-3 py-3 text-center">
                <p className="text-xl font-semibold text-white">{c.loyalty_points.toLocaleString()}</p>
                <p className="text-gray-600 text-xs mt-0.5">Loyalty Points</p>
              </div>
              <div className="bg-[#1a1a1a] rounded-lg px-3 py-3 text-center">
                <p className="text-xl font-semibold text-white">{(c.drops_balance ?? 0).toLocaleString()}</p>
                <p className="text-gray-600 text-xs mt-0.5">Drops</p>
              </div>
            </div>

            {/* Info */}
            <div className="bg-[#1a1a1a] rounded-lg px-4 py-3 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Plan</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.plan === 'premium' ? 'bg-yellow-500/15 text-yellow-400' : 'bg-gray-500/15 text-gray-400'}`}>
                  {c.plan === 'premium' ? '★ Premium' : 'Free'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Status</span>
                {c.frozen
                  ? <span className="text-xs bg-red-500/15 text-red-400 px-2 py-0.5 rounded-full">Frozen</span>
                  : <span className="text-xs bg-green-500/15 text-green-400 px-2 py-0.5 rounded-full">Active</span>
                }
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Member since</span>
                <span className="text-gray-300">{formatDate(c.created_at)}</span>
              </div>
            </div>

            {/* Store credit */}
            <StoreCreditSection customerId={c.id} canManage={canManage} />

            {/* Actions */}
            {canManage && (
              <div>
                <p className="text-xs text-gray-600 uppercase tracking-wider mb-2">Actions</p>
                <button
                  onClick={() => toggleFreeze.mutate({ id: c.id, frozen: !c.frozen })}
                  disabled={toggleFreeze.isPending}
                  className={`w-full text-sm py-2 rounded-lg border transition-colors disabled:opacity-50 ${
                    c.frozen
                      ? 'border-green-500/30 text-green-400 hover:bg-green-500/10'
                      : 'border-red-500/30 text-red-400 hover:bg-red-500/10'
                  }`}
                >
                  {toggleFreeze.isPending ? '…' : c.frozen ? 'Unfreeze Account' : 'Freeze Account'}
                </button>
              </div>
            )}

            {/* Order history */}
            <section>
              <p className="text-xs text-gray-600 uppercase tracking-wider mb-3">Recent Orders</p>
              {data!.orders.length === 0 ? (
                <p className="text-gray-600 text-sm text-center py-4">No orders yet</p>
              ) : (
                <div className="space-y-2">
                  {data!.orders.map(order => (
                    <div key={order.id} className="bg-[#1a1a1a] rounded-lg px-3 py-2.5 flex items-center justify-between">
                      <div>
                        <p className="text-white text-sm font-mono">{order.order_number}</p>
                        <p className="text-gray-600 text-xs mt-0.5">{formatDateTime(order.created_at)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-gray-300 text-sm">{formatCurrency(order.total_fils)}</p>
                        <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${STATUS_COLORS[order.status] ?? 'bg-gray-500/15 text-gray-400'}`}>
                          {STATUS_LABELS[order.status] ?? order.status}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  )
}

export default function Customers() {
  const { can } = useAuth()
  const qc = useQueryClient()
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const params = new URLSearchParams()
  if (search) params.set('search', search)
  params.set('page', String(page))

  const { data, isError } = useQuery<{ customers: Customer[]; total: number; limit: number }>({
    queryKey: ['customers', search, page],
    queryFn: () => api.get(`/customers?${params}`).then(r => r.data),
  })

  const totalPages = Math.ceil((data?.total ?? 0) / (data?.limit ?? 50))
  const canManage = can(['super_admin', 'operations'])

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-lg font-semibold text-white">Customers</h1>
        <CsvControls
          basePath="/customers"
          templateFilename="customers-template.csv"
          exportFilename={`customers-export-${new Date().toISOString().slice(0, 10)}.csv`}
          label="customer"
          onImported={() => qc.invalidateQueries({ queryKey: ['customers'] })}
        />
      </div>
      {isError && <div className="mb-4 text-red-400 text-sm bg-red-400/10 rounded-lg px-4 py-3">Failed to load customers. Check your connection or try refreshing.</div>}

      <div className="flex gap-3 mb-5">
        <input
          value={search} onChange={e => { setSearch(e.target.value); setPage(1) }}
          placeholder="Search name, phone…"
          className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#C41E3A] w-64"
        />
      </div>

      <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-gray-600 border-b border-[#2e2e2e]">
              {['Customer', 'Plan', 'Orders', 'Total Spent', 'Points', 'Drops', 'Joined', 'Status'].map(h => (
                <th key={h} className="px-4 py-3 text-left font-medium">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data?.customers.map((c) => (
              <tr
                key={c.id}
                className={`border-b border-[#222] hover:bg-white/2 transition-colors cursor-pointer ${c.frozen ? 'opacity-60' : ''}`}
                onClick={() => setSelectedId(c.id)}
              >
                <td className="px-4 py-3">
                  <p className="text-white">{c.full_name}</p>
                  <p className="text-gray-500 text-xs">{c.phone}</p>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.plan === 'premium' ? 'bg-yellow-500/15 text-yellow-400' : 'bg-gray-500/15 text-gray-400'}`}>
                    {c.plan === 'premium' ? '★ Premium' : 'Free'}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-300">{c.order_count}</td>
                <td className="px-4 py-3 text-gray-300">{formatCurrency(c.total_spent_fils)}</td>
                <td className="px-4 py-3 text-gray-400">{c.loyalty_points.toLocaleString()}</td>
                <td className="px-4 py-3 text-gray-400">{(c.drops_balance ?? 0).toLocaleString()}</td>
                <td className="px-4 py-3 text-gray-500 text-xs">{formatDate(c.created_at)}</td>
                <td className="px-4 py-3">
                  {c.frozen
                    ? <span className="text-xs bg-red-500/15 text-red-400 px-2 py-0.5 rounded-full">Frozen</span>
                    : <span className="text-xs bg-green-500/15 text-green-400 px-2 py-0.5 rounded-full">Active</span>
                  }
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-[#2e2e2e] text-xs text-gray-500">
            <span>{data?.total} total customers</span>
            <div className="flex gap-2">
              <button disabled={page === 1} onClick={() => setPage(p => p - 1)} className="px-3 py-1 rounded bg-[#2e2e2e] disabled:opacity-40 hover:bg-[#3a3a3a] transition-colors">Prev</button>
              <span className="px-2 py-1">{page} / {totalPages}</span>
              <button disabled={page === totalPages} onClick={() => setPage(p => p + 1)} className="px-3 py-1 rounded bg-[#2e2e2e] disabled:opacity-40 hover:bg-[#3a3a3a] transition-colors">Next</button>
            </div>
          </div>
        )}
      </div>

      {selectedId && (
        <CustomerPanel
          customerId={selectedId}
          onClose={() => setSelectedId(null)}
          canManage={canManage}
        />
      )}
    </div>
  )
}

interface StoreCreditTx {
  id: string
  amount_fils: number
  source: string
  source_order_id: string | null
  notes: string | null
  expires_at: string | null
  created_at: string
  created_by_name: string | null
}

interface StoreCreditResponse {
  balance_fils: number
  transactions: StoreCreditTx[]
}

const STORE_CREDIT_SOURCES = [
  { value: 'refund_cod', label: 'COD refund' },
  { value: 'goodwill', label: 'Goodwill' },
  { value: 'admin_adjust', label: 'Manual adjustment' },
] as const

function StoreCreditSection({ customerId, canManage }: { customerId: string; canManage: boolean }) {
  const qc = useQueryClient()
  const { data } = useQuery<StoreCreditResponse>({
    queryKey: ['customer-store-credit', customerId],
    queryFn: () => api.get(`/customers/${customerId}/store-credit`).then(r => r.data),
  })
  const [addOpen, setAddOpen] = useState(false)
  const balance = data?.balance_fils ?? 0
  const recent = (data?.transactions ?? []).slice(0, 5)

  return (
    <section>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs text-gray-600 uppercase tracking-wider">Store credit</p>
        {canManage && (
          <button
            onClick={() => setAddOpen(true)}
            className="text-xs text-[#C41E3A] hover:underline"
          >
            + Add credit
          </button>
        )}
      </div>
      <div className="bg-[#1a1a1a] rounded-lg px-4 py-3">
        <p className="text-xl font-semibold text-white">{formatCurrency(balance)}</p>
        <p className="text-gray-600 text-xs mt-0.5">Available balance</p>
        {recent.length > 0 && (
          <div className="mt-3 pt-3 border-t border-[#2e2e2e] space-y-1.5">
            {recent.map(tx => (
              <div key={tx.id} className="flex items-center justify-between text-xs">
                <div className="flex-1 truncate">
                  <span className="text-gray-300">{tx.source}</span>
                  {tx.notes && <span className="text-gray-600"> · {tx.notes}</span>}
                </div>
                <span className={`font-mono ml-2 ${tx.amount_fils > 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {tx.amount_fils > 0 ? '+' : '−'}{formatCurrency(Math.abs(tx.amount_fils))}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {addOpen && (
        <AddStoreCreditModal
          customerId={customerId}
          onClose={() => setAddOpen(false)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['customer-store-credit', customerId] })
            setAddOpen(false)
          }}
        />
      )}
    </section>
  )
}

function AddStoreCreditModal({
  customerId,
  onClose,
  onSaved,
}: {
  customerId: string
  onClose: () => void
  onSaved: () => void
}) {
  const [amount, setAmount] = useState('')
  const [source, setSource] = useState<string>('refund_cod')
  const [sourceOrderId, setSourceOrderId] = useState('')
  const [notes, setNotes] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    const jd = parseFloat(amount)
    if (!Number.isFinite(jd) || jd <= 0) {
      setError('Amount must be a positive number.')
      return
    }
    const fils = Math.round(jd * 1000)
    setSubmitting(true)
    try {
      await api.post(`/customers/${customerId}/store-credit`, {
        amount_fils: fils,
        source,
        source_order_id: sourceOrderId.trim() || null,
        notes: notes.trim() || null,
      })
      onSaved()
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } } }
      setError(e.response?.data?.message ?? 'Couldn\'t add credit.')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4">
      <form onSubmit={onSubmit} className="w-full max-w-sm bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-white text-sm font-semibold">Add store credit</h3>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-white">
            <X size={16} />
          </button>
        </div>

        <div>
          <label className="text-xs text-gray-500">Amount (JOD)</label>
          <input
            type="text"
            inputMode="decimal"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            placeholder="e.g. 5.000"
            className="w-full mt-1 bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C41E3A]"
            autoFocus
          />
        </div>

        <div>
          <label className="text-xs text-gray-500">Reason</label>
          <select
            value={source}
            onChange={e => setSource(e.target.value)}
            className="w-full mt-1 bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C41E3A]"
          >
            {STORE_CREDIT_SOURCES.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>

        {source === 'refund_cod' && (
          <div>
            <label className="text-xs text-gray-500">Source order ID (optional)</label>
            <input
              type="text"
              value={sourceOrderId}
              onChange={e => setSourceOrderId(e.target.value)}
              placeholder="Original order id"
              className="w-full mt-1 bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-[#C41E3A]"
            />
          </div>
        )}

        <div>
          <label className="text-xs text-gray-500">Notes (optional)</label>
          <textarea
            value={notes}
            onChange={e => setNotes(e.target.value)}
            rows={2}
            placeholder="Surfaced to the customer on the wallet history."
            className="w-full mt-1 bg-[#242424] border border-[#3a3a3a] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#C41E3A] resize-none"
          />
        </div>

        {error && <p className="text-xs text-red-400">{error}</p>}

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 text-sm py-2 rounded-lg border border-[#3a3a3a] text-gray-400 hover:bg-[#242424]"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="flex-1 text-sm py-2 rounded-lg bg-[#C41E3A] text-white font-medium hover:bg-[#a01830] disabled:opacity-50"
          >
            {submitting ? 'Adding…' : 'Add credit'}
          </button>
        </div>
      </form>
    </div>
  )
}
